import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Calendar, ArrowRight, Sparkles } from 'lucide-react'
import { useProjects } from '@/hooks/useProjects'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'
import type { Language } from '@/components/LanguageWrapper'
import { PageLayout } from '@/components/PageLayout'
import { Meta } from '@/components/MetaManager'
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData'

const HUNDRED_CATEGORIES = ['launch100', 'spy100', 'ship100', 'grow100'] as const

type HundredCategoryId = (typeof HUNDRED_CATEGORIES)[number]

interface TimelineItem {
  id: string
  title: string
  description: string
  categoryId: HundredCategoryId
  startDate: string | null
  endDate: string | null
  primaryDate: Date | null
  image: string | null
  status: string
}

function formatDate(value: string | null, lang: Language) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
}

const categoryStyles = {
  spy100: { 
    color: 'text-orange-400', 
    bg: 'bg-orange-500/20', 
    border: 'border-orange-500/30',
    glow: 'shadow-orange-500/20'
  },
  launch100: { 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/20', 
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20'
  },
  ship100: { 
    color: 'text-emerald-400', 
    bg: 'bg-emerald-500/20', 
    border: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/20'
  },
  grow100: { 
    color: 'text-purple-400', 
    bg: 'bg-purple-500/20', 
    border: 'border-purple-500/30',
    glow: 'shadow-purple-500/20'
  },
}

const statusStyles = {
  completed: { color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  'in-progress': { color: 'text-blue-400', bg: 'bg-blue-500/20' },
  planned: { color: 'text-purple-400', bg: 'bg-purple-500/20' },
}

export default function ChallengeJourney() {
  const { data: projects, isLoading } = useProjects()
  const { t, i18n } = useTranslation()
  const { getLocalizedPath } = useLocalizedPath()
  const language = i18n.language as Language
  const metaTitle = `${t.collections.challenge100.journey.title} | borninsea`;
  const metaDesc = t.collections.challenge100.journey.subtitle;
  const metaKeywords = language === 'zh'
    ? 'Challenge100,时间线,项目进度,里程碑'
    : 'Challenge100,timeline,project progress,milestones';

  const hundredProjects = (projects || []).filter((project) =>
    HUNDRED_CATEGORIES.includes(project.category_id as HundredCategoryId),
  )

  const items: TimelineItem[] = hundredProjects
    .map((project) => {
      const primary = project.start_date || project.end_date
      return {
        id: project.id,
        title: project.title,
        description: project.description,
        categoryId: project.category_id as HundredCategoryId,
        startDate: project.start_date,
        endDate: project.end_date,
        primaryDate: primary ? new Date(primary) : null,
        image: project.image,
        status: project.status,
      }
    })
    .sort((a, b) => {
      const aTime = a.primaryDate ? a.primaryDate.getTime() : 0
      const bTime = b.primaryDate ? b.primaryDate.getTime() : 0
      return bTime - aTime // 最新的在前
    })

  const breadcrumbs = generateBreadcrumbs([
    { label: 'Challenge 100', path: '/challenge100' },
    { label: language === 'zh' ? '时间线' : 'Journey', path: '/challenge100/journey' }
  ], language);

  return (
    <PageLayout>
      <Meta title={metaTitle} description={metaDesc} keywords={metaKeywords} />
      <StructuredData type="website" breadcrumbs={breadcrumbs} />
      {/* Hero Section - 全屏暗色背景 */}
      <div className="relative min-h-[50vh] bg-black overflow-hidden">
        {/* 背景渐变效果 */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/10 via-transparent to-accent-purple/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent-emerald/5 via-transparent to-transparent" />
        
        {/* 装饰性光效 */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-blue/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent-purple/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* 内容 */}
        <div className="relative z-10 container mx-auto px-6 sm:px-8 lg:px-12 pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 glass-effect px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-accent-blue" />
              <span className="text-sm font-medium text-white/80">Challenge 100 · Journey</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white leading-tight">
              {t.collections.challenge100.journey.title}
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl">
              {t.collections.challenge100.journey.subtitle}
            </p>
            
            <div className="flex items-center gap-4 mt-8">
              <div className="glass-effect px-4 py-2 rounded-lg">
                <span className="text-2xl font-bold text-white">{items.length}</span>
                <span className="text-white/60 ml-2 text-sm">{t.collections.challenge100.journey.projectCount}</span>
              </div>
              <Link 
                to={getLocalizedPath('/challenge100')}
                className="glass-effect px-4 py-2 rounded-lg flex items-center gap-2 text-white/80 hover:text-white hover:bg-white/10 transition-all"
              >
                <span className="text-sm">{t.collections.challenge100.cta.primary}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Timeline Section */}
      <div className="bg-background py-16">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="glass-effect p-6 rounded-2xl">
                <div className="animate-spin w-8 h-8 border-2 border-accent-blue border-t-transparent rounded-full" />
              </div>
            </div>
          ) : items.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="glass-effect inline-block p-8 rounded-2xl">
                <p className="text-muted-foreground">{t.collections.challenge100.journey.noDate}</p>
              </div>
            </motion.div>
          ) : (
            <div className="relative">
              {/* 中央时间线 */}
              <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue/50 via-accent-purple/50 to-accent-emerald/50 hidden lg:block" />
              
              {/* 移动端时间线 */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-accent-blue/50 via-accent-purple/50 to-accent-emerald/50 lg:hidden" />

              <div className="space-y-8 lg:space-y-12">
                {items.map((item, index) => {
                  const startLabel = formatDate(item.startDate, language)
                  const endLabel = formatDate(item.endDate, language)
                  const catStyle = categoryStyles[item.categoryId]
                  const statStyle = statusStyles[item.status as keyof typeof statusStyles] || statusStyles.planned
                  const isLeft = index % 2 === 0

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, margin: '-50px' }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className={`relative flex items-start gap-6 ${
                        isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      {/* 时间线节点 - 桌面端 */}
                      <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 z-10">
                        <div className={`w-4 h-4 rounded-full ${catStyle.bg} ${catStyle.border} border-2 shadow-lg ${catStyle.glow}`} />
                      </div>
                      
                      {/* 时间线节点 - 移动端 */}
                      <div className="lg:hidden absolute left-6 transform -translate-x-1/2 z-10">
                        <div className={`w-3 h-3 rounded-full ${catStyle.bg} ${catStyle.border} border-2`} />
                      </div>

                      {/* 卡片 */}
                      <div className={`flex-1 ml-10 lg:ml-0 ${isLeft ? 'lg:pr-12 lg:text-right' : 'lg:pl-12'}`}>
                        <div className="group bg-card border border-border rounded-2xl p-5 hover:border-primary/30 hover:shadow-xl transition-all duration-300 cursor-pointer">
                          <div className={`flex items-start gap-4 ${isLeft ? 'lg:flex-row-reverse' : ''}`}>
                            {/* 图片 */}
                            {item.image && (
                              <div className="flex-shrink-0">
                                <img 
                                  src={item.image} 
                                  alt={item.title}
                                  className="w-16 h-16 rounded-xl object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            )}
                            
                            <div className={`flex-1 min-w-0 ${isLeft ? 'lg:text-right' : ''}`}>
                              {/* 标签 */}
                              <div className={`flex items-center gap-2 mb-2 flex-wrap ${isLeft ? 'lg:justify-end' : ''}`}>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catStyle.bg} ${catStyle.color}`}>
                                  {t.categories[item.categoryId]}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statStyle.bg} ${statStyle.color}`}>
                                  {t.status[item.status as keyof typeof t.status] || item.status}
                                </span>
                              </div>
                              
                              {/* 标题 */}
                              <h3 className="font-bold text-foreground group-hover:text-primary transition-colors mb-1 line-clamp-1">
                                {item.title}
                              </h3>
                              
                              {/* 描述 */}
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {item.description}
                              </p>
                              
                              {/* 日期 */}
                              <div className={`flex items-center gap-1 text-xs text-muted-foreground ${isLeft ? 'lg:justify-end' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                <span>
                                  {startLabel || endLabel || t.collections.challenge100.journey.noDate}
                                  {startLabel && endLabel && startLabel !== endLabel && (
                                    <span> → {endLabel}</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* 占位符 - 保持对称 */}
                      <div className="hidden lg:block flex-1" />
                    </motion.div>
                  )
                })}
              </div>
              
              {/* 时间线结束标记 */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="flex justify-center mt-12"
              >
                <div className="glass-effect px-6 py-3 rounded-full">
                  <p className="text-sm text-muted-foreground italic flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-accent-purple" />
                    {i18n.language === 'zh' ? '更多项目正在路上...' : 'More projects coming soon...'}
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  )
}
